import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';
import api from '../services/api';
import { blankPlan, normalize, summarize } from '../implementation/catalog';
import { downloadPlanPdf } from '../implementation/downloadPlan';

// Sección del detalle de cuenta, sólo para Enterprise: crea el plan de
// implementación o lleva a él, con su avance a la vista.
export default function AccountImplementation({ tenantId, source, account, owners }) {
  const { t, fmtDate } = useI18n();
  const navigate = useNavigate();
  const canEdit = useCan('edit_cs');

  const [plan, setPlan] = useState(undefined);   // undefined = cargando, null = sin plan
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const open = () => navigate(`/accounts/${tenantId}/implementation?source=${source}`);

  useEffect(() => {
    api.get(`/api/accounts/${tenantId}/implementation-plan`, { params: { source } })
      .then((res) => setPlan(res.data))
      .catch(() => { setPlan(null); setError(t('implementation.loadError')); });
  }, [tenantId, source, t]);

  async function create() {
    setBusy(true);
    setError(null);
    // Se arranca con lo que la herramienta ya sabe de la cuenta.
    const data = blankPlan();
    data.cliente = account.display_name || account.organization_name || '';
    data.csm = owners.find((o) => o.id === account.cs_owner_id)?.display_name || '';
    try {
      await api.post(`/api/accounts/${tenantId}/implementation-plan`, { data }, { params: { source } });
      open();
    } catch (err) {
      // Si alguien lo creó mientras tanto, basta con abrirlo.
      if (err.response?.status === 409) open();
      else setError(t('implementation.createError'));
      setBusy(false);
    }
  }

  async function exportPdf() {
    setBusy(true);
    setError(null);
    try {
      await downloadPlanPdf(normalize(plan.data));
    } catch (err) {
      console.error('downloadPlanPdf:', err);
      setError(t('implementation.exportError'));
    } finally {
      setBusy(false);
    }
  }

  const progress = plan ? summarize(normalize(plan.data)).progress : null;

  return (
    <Box>
      <Typography sx={{
        fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: '#847782', mb: 1.2,
      }}>{t('implementation.title')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 1.2 }} onClose={() => setError(null)}>{error}</Alert>}

      {plan === null && (
        <Box>
          <Typography sx={{ fontSize: 13.5, color: '#847782', mb: 1.2 }}>
            {t('implementation.empty')}
          </Typography>
          {canEdit && (
            <Button size="small" variant="contained" onClick={create} disabled={busy}>
              {t('implementation.add')}
            </Button>
          )}
        </Box>
      )}

      {plan && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
            <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3, backgroundColor: '#EDE6E3', overflow: 'hidden' }}>
              <Box sx={{ width: `${progress.pct}%`, height: '100%', backgroundColor: '#237A55' }} />
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {progress.pct}%
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: '#847782', mb: 1.4 }}>
            {t('implementation.summary', {
              done: progress.done, total: progress.total, date: fmtDate(plan.updated_at),
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={open}>{t('implementation.open')}</Button>
            <Button size="small" variant="outlined" onClick={exportPdf} disabled={busy}>
              {t('implementation.exportPdf')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
